import React, { useState, useCallback, useRef } from "react";
import { useDropzone, FileRejection, DropzoneOptions } from "react-dropzone";
import { showNotification, getGlobalconst, formatBytes } from "../../../taskpane/utiltyfunctions";
import {
  NOTIF_FAIL,
  NOTIF_SUCCESS,
  PROPBACKUPID,
  PROPCALLBACKTOKEN,
  PROPJWTTOKEN,
  PROPEMAILUNIQUEID,
  ChunkUploadData,
  PROPMAILID,
  NOTIF_WARN,
  OutlookVO,
  FileUploadState,
  UploadVO,
} from "../../../taskpane/globalconstants";
import {
  FiFile,
  FiFileText,
  FiImage,
  FiVideo,
  FiArchive,
  FiMusic,
  FiX,
  FiUploadCloud,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi"; // Using react-icons for file icons
import "./app.css"; // Your main app styles
import { v5 as uuidv5 } from "uuid";
import { uploadChunk } from "./uploadChunks";
import { getResolveURL } from "../../Scripts/resolveURL";
import { makeSecured2FetchRequest, servicecallsecurity } from "../../../taskpane/wscall-helper";

// --- Configuration ---
var CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks (use Infinity for whole file upload)
const CHUNK_NORM_SIZE = 5 * 1024 * 1024; // 5MB chunks (use Infinity for whole file upload)
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB max file size (optional)
const SIMULTANEOUS_UPLOADS = 3; // Optional: Limit concurrent uploads

function getFileIcon(fileType?: string): JSX.Element {
  if (!fileType) return <FiFile className="text-gray-500" />;
  if (fileType.startsWith("image/")) return <FiImage className="text-blue-500" />;
  if (fileType.startsWith("video/")) return <FiVideo className="text-purple-500" />;
  if (fileType.startsWith("audio/")) return <FiMusic className="text-pink-500" />;
  if (fileType === "application/pdf") return <FiFileText className="text-red-500" />;
  if (
    [
      "application/zip",
      "application/x-rar-compressed",
      "application/x-7z-compressed",
      "application/gzip",
      "application/x-tar",
    ].some((t) => fileType === t)
  )
    return <FiArchive className="text-yellow-600" />;
  if (fileType.startsWith("text/")) return <FiFileText className="text-gray-700" />;
  return <FiFile className="text-gray-500" />; // Default icon
}

// --- Component ---
const ChunkedUploader: React.FC = () => {
  const [filesToUpload, setFilesToUpload] = useState<FileUploadState[]>([]);
  const fileCounterRef = useRef(0);
  const activeUploadsCountRef = useRef(0);
  // Stores cancel tokens for active/pending uploads { id: CancelTokenSource }
  const cancelTokensRef = useRef<Map<string, CancelTokenSource>>(new Map());

  // --- State Update Helper ---
  const updateFileState = (id: string, updates: Partial<FileUploadState>) => {
    setFilesToUpload((currentFiles) => currentFiles.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  // --- Queue Management ---
  const processQueue = useCallback(
    () => {
      const currentlyUploading = activeUploadsCountRef.current;
      if (currentlyUploading >= SIMULTANEOUS_UPLOADS) {
        return; // Wait if concurrency limit reached
      }

      let startedUpload = false;
      setFilesToUpload((currentFiles) => {
        // Find the next queued file
        const nextFileIndex = currentFiles.findIndex((f) => f.status === "queued");
        if (nextFileIndex === -1 || startedUpload) {
          return currentFiles; // No queued files or already started one this cycle
        }

        const fileToStart = currentFiles[nextFileIndex];
        activeUploadsCountRef.current++;
        startedUpload = true; // Mark that we are starting one

        // Update state immediately to 'pending' (preparing to upload)
        const updatedFiles = currentFiles.map((f, index) =>
          index === nextFileIndex ? { ...f, status: "pending" } : f
        );

        // Start the actual upload process asynchronously
        // Use setTimeout to ensure state update happens before upload logic
        setTimeout(() => {
          startUpload(fileToStart);
        }, 0);

        return updatedFiles;
      });
    },
    [
      /* Dependencies: startUpload - needs to be stable */
    ]
  ); // Add startUpload dependency if it changes

  // --- Cleanup and Next Upload ---
  const handleUploadFinished = (id: string, finalStatus: UploadStatus) => {
    cancelTokensRef.current.set(id, "pending"); // Store cancel token immediately
    activeUploadsCountRef.current--;
    cancelTokensRef.current.delete(id); // Clean up cancel token
    updateFileState(id, { status: finalStatus });
    processQueue(); // Check if another file can start
    showNotification(NOTIF_SUCCESS, `Upload file successfull`);
  };

  // --- Upload Logic (Chunked or Whole) ---
  const uploadFile = async (fileState: FileUploadState) => {
    const { file, id, isChunked } = fileState;

    // prep the UUID Namespace
    const MY_NAMESPACE = uuidv5.URL; // Or use uuidv5.URL
    const uuid: string = uuidv5(file.name, MY_NAMESPACE);
    try {
      // update our token
      await servicecallsecurity(getGlobalconst(PROPCALLBACKTOKEN));

      //if (!cancelTokensRef.current.has(id)) {
      //  console.warn(`Upload cancelled or removed before starting: ${id}`);
      //  handleUploadFinished(id, "cancelled"); // Ensure queue processes
      //  return;
      //}

      //const cancelSource = cancelTokensRef.current.get(id);
      updateFileState(uuid, { status: "uploading", progress: 0 });

      //if (isChunked) {
      // --- Chunked Upload ---
      if (fileState.totalChunks == undefined) {
        fileState.totalChunks = 0;
      }
      //const backupidfoldername = getGlobalconst(PROPBACKUPID) + "." + convertToDateAndFormat("NOW", "ddyymmssms");
      for (let i = 0; i < fileState.totalChunks; i++) {
        const response: any = await uploadChunkRecursive(fileState, i, getGlobalconst(PROPBACKUPID));
        if (response.result != "0") {
          //reject("HTTP error! status: ${response.status}");
        }
      }

      // complete the file updloads
      // All chunks uploaded successfully, send done message.
      const queryParams: { [key: string]: string } = {
        dzIdentifier: id,
        //fileName: encodeURIComponent(file.name),
        fileName: file.name,
        expectedBytes: file.size.toString(),
        totalChunks: fileState.totalChunks.toString(),
        userID: "",
        backupid: getGlobalconst(PROPBACKUPID),
        msid: getGlobalconst(PROPMAILID),
      };

      // get the URL for the service call.
      const uri = getResolveURL("UPLOADCHUNKSMERGE");

      const dzResult: UploadVO = await makeSecured2FetchRequest(
        getGlobalconst(PROPCALLBACKTOKEN),
        getGlobalconst(PROPJWTTOKEN),
        uri,
        queryParams
      );

      //make sure the call was good.
      if (dzResult.result == 0) {
        // all done uploading show sucess sign (DO ERROR Check first)
        handleUploadFinished(id, "completed");
      } else {
        // all done uploading show sucess sign (DO ERROR Check first)
        handleUploadError(null, id, file.name, `issue same file uploaded not supported ${file.name}`);
      }
    } catch (error) {
      handleUploadError(error, id, file.name, `Error with upload try again ${file.name}`);
    }
  };

  async function uploadChunkRecursive(
    fileState: FileUploadState,
    chunkIndex: number,
    backupidfoldername: string
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      //const uploadChunkRecursive = async (fileState: FileUploadState, chunkIndex: number) => {
      const { file, id, totalChunks = 0 } = fileState; // Default totalChunks to 0 if undefined
      try {
        // Double-check cancellation before proceeding
        //if (!cancelTokensRef.current.has(id)) {
        //  console.log(`Upload cancelled before chunk ${chunkIndex} of ${id}`);
        //  // Don't call handleUploadFinished here, it's handled by the cancellation flow
        //  return;
        //}
        //const cancelSource = cancelTokensRef.current.get(uuid);

        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        //const formData = new FormData();

        const uploadData: ChunkUploadData = {
          backupid: backupidfoldername,
          msid: getGlobalconst(PROPEMAILUNIQUEID),
          dzUuid: id, // A unique ID for the whole upload session
          dzChunkIndex: String(chunkIndex),
          dzTotalFileSize: String(file.size),
          dzCurrentChunkSize: String(chunk.size),
          dzTotalChunkCount: String(totalChunks),
          dzChunkByteOffset: String(chunkIndex * CHUNK_SIZE),
          dzChunkSize: String(CHUNK_SIZE), // The intended chunk size
          dzFilename: file.name,
          fileChunk: chunk,
        };

        const response: any = await uploadChunk(file, uploadData, chunk);
        if (response.result != 0) {
          showNotification(NOTIF_FAIL, "Requested Upload file was not succesful");
          reject(-1);
        } else {
          const newUploadedChunks = (fileState.uploadedChunks ?? 0) + 1;
          updateFileState(id, { uploadedChunks: newUploadedChunks }); // Update chunk count
          if (newUploadedChunks < totalChunks) {
            //const currentFileState = currentFiles.find((f) => f.id === id);
          }
          resolve(response);
        }
      } catch (error) {
        handleUploadError(error, id, file.name, `chunk ${chunkIndex}`);
        reject(error);
      }
    });
  }

  /*** OLD CODE */
  /*
    formData.append("chunk", chunk, `${file.name}.part${chunkIndex}`);
    formData.append("fileId", id);
    formData.append("chunkIndex", String(chunkIndex));
    formData.append("totalChunks", String(totalChunks));
    formData.append("originalFilename", file.name);
    formData.append("fileSize", String(file.size));
    formData.append("fileType", file.type);

 
      await axios.post(UPLOAD_URL, formData, {
        // Ensure this URL handles chunks
        headers: { "Content-Type": "multipart/form-data" },
        cancelToken: cancelSource?.token,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          const loaded = progressEvent.loaded ?? 0;
          const chunkProgress = Math.round((loaded * 100) / chunk.size);
          // Calculate overall progress more accurately
          const overallProgress = Math.round(
            (((fileState.uploadedChunks ?? 0) * CHUNK_SIZE + loaded) / file.size) * 100
          );
          updateFileState(id, { progress: Math.min(overallProgress, 100) });
          // console.log(`Chunk ${chunkIndex}/${totalChunks-1} progress: ${chunkProgress}% | Overall: ${overallProgress}%`);
        },
      });

      // --- Chunk Success ---
      // Check again if cancelled *during* the request
      if (!cancelTokensRef.current.has(id)) return;

      const newUploadedChunks = (fileState.uploadedChunks ?? 0) + 1;
      updateFileState(id, { uploadedChunks: newUploadedChunks }); // Update chunk count

      if (newUploadedChunks < totalChunks) {
        // Schedule next chunk upload (using updated state via finding it again)
        setFilesToUpload((currentFiles) => {
          const currentFileState = currentFiles.find((f) => f.id === id);
          if (currentFileState && currentFileState.status === "uploading") {
            // Use setTimeout to avoid potential stack overflow on many chunks
            setTimeout(() => uploadChunkRecursive(currentFileState, chunkIndex + 1), 0);
          }
          return currentFiles; // Return unchanged if file not found or status changed
        });
      } else {
        // All chunks uploaded
        console.log(`File ${file.name} (ID: ${id}) chunked upload complete!`);
        handleUploadFinished(id, "completed");
        // Optional: Notify server completion: axios.post('/api/upload-complete', { fileId: id });
      }
      */

  //}

  // --- Error Handling ---
  const handleUploadError = (error: unknown, id: string, filename: string, context: string) => {
    showNotification(NOTIF_WARN, `Upload file had issues ${filename} (${context})`);
    showNotification(NOTIF_WARN, `You must secure your main message before attachments`);
    handleRemoveFile(id);
    /*
    if (axios.isCancel(error)) {
      showNotification(NOTIF_WARN, `Upload cancelled for ${filename} (${context})`);
      // State should already be 'cancelled' or will be updated by handleRemoveFile
      // We still need to ensure the queue progresses if it was an active upload
      if (activeUploadsCountRef.current > 0 && cancelTokensRef.current.has(id)) {
        handleUploadFinished(id, "cancelled");
      } else {
        // If it was already removed/cancelled, just ensure the token is gone
        cancelTokensRef.current.delete(id);
      }
    } else {
      const axiosError = error as AxiosError; // Type assertion
      console.error(`Error uploading ${filename} (${context}):`, axiosError);
      const errorMessage = (axiosError.response?.data as any)?.message || axiosError.message || "Unknown upload error";
      // Only update state if the file hasn't been removed already
      if (cancelTokensRef.current.has(id)) {
        updateFileState(id, { status: "failed", errorMessage });
        handleUploadFinished(id, "failed"); // Mark as finished (failed)
      }
    }
    */
  };

  // --- Prepare and Start Upload ---
  const startUpload = useCallback(
    (fileState: FileUploadState) => {
      //if (!cancelTokensRef.current.has(fileState.id)) {
      //  console.warn("Attempted to start upload for a removed/cancelled file:", fileState.id);
      //  return; // Already cancelled/removed
      //}
      //console.log(`Starting upload for: ${fileState.file.name} (ID: ${fileState.id})`);
      uploadFile(fileState);
    },
    [
      /* uploadFile dependency if needed, but it should be stable */
    ]
  );

  // --- react-dropzone Callback ---
  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      //console.log("Accepted Files:", acceptedFiles);
      //console.log("Rejected Files:", fileRejections);
      CHUNK_SIZE = CHUNK_NORM_SIZE;

      fileRejections.forEach((rejection) => {
        //console.warn(`File rejected: ${rejection.file.name} - ${rejection.errors.map((e) => e.message).join(", ")}`);
        // TODO: Show user-friendly rejection messages in the UI
      });

      const newFiles: FileUploadState[] = acceptedFiles.map((file) => {
        if (file.size <= CHUNK_SIZE) {
          CHUNK_SIZE = Math.ceil(file.size / 2);
        }

        const id = `file-${file.name}-${Date.now()}-${fileCounterRef.current++}`;
        const isChunked = file.size > CHUNK_SIZE; // Determine if chunking is needed
        const totalChunks = isChunked ? Math.ceil(file.size / CHUNK_SIZE) : undefined;

        return {
          id,
          file,
          status: "queued", // Start as queued
          progress: 0,
          totalChunks,
          uploadedChunks: 0,
          //cancelSource: source, // Keep for potential direct cancellation access if needed elsewhere
          errorMessage: undefined,
          isChunked,
        };
      });

      setFilesToUpload((currentFiles) => [...currentFiles, ...newFiles]);

      // Trigger queue processing after state update
      setTimeout(processQueue, 0);
    },
    [processQueue]
  ); // Added processQueue dependency

  // --- Remove/Cancel File ---
  const handleRemoveFile = async (id: string) => {
    //console.log(`Attempting to remove/cancel file: ${id}`);
    const cancelSource = cancelTokensRef.current.get(id);
    // Find the file's current status
    const fileToRemove = filesToUpload.find((f) => f.id === id);

    // Remove from UI state immediately
    setFilesToUpload((currentFiles) => currentFiles.filter((f) => f.id !== id));

    let wasUploading = false;
    if (cancelSource) {
      if (fileToRemove?.status === "uploading" || fileToRemove?.status === "pending") {
        wasUploading = true;
      }
      showNotification(NOTIF_FAIL, "User cancelled upload.");
      //cancelSource.cancel("User cancelled upload."); // Send cancellation signal
      cancelTokensRef.current.delete(id); // Remove token from tracking
      //console.log(`Cancellation requested via Axios for ${id}`);
    }

    // delete the requested file
    const queryParams: { [key: string]: string } = {
      backupid: getGlobalconst(PROPBACKUPID),
      filename: fileToRemove,
      format: "json",
    };

    try {
      var uri = await getResolveURL("URLDELETE");
      const respdata: OutlookVO = await makeSecured2FetchRequest(
        getGlobalconst(PROPCALLBACKTOKEN),
        getGlobalconst(PROPJWTTOKEN),
        uri,
        queryParams
      );

      // determine what to do on a non-delete delete request.
      if (respdata && respdata.result == 0) {
        //showNotification(NOTIF_FAIL, "Error - Could not delete file " + fileToRemove);
      } else {
        // if unable to delete the file from the server, never got there so ignore error.
        //throw new Error("Secured2 getTemplates Experienced Error. No response");
      }
    } catch (error) {
      cancelTokensRef.current.delete(id); // Remove token from tracking
      //var description = "Secured2 getTemplates Experienced Error.  Return:" + error;
      //showNotification(NOTIF_FAIL, "Error - " + description);
      //throw new Error(description);
    }

    //************************ DELETE LOGIC HERE **********************/

    // If it was an active upload, decrement count and process queue
    // Check status *before* filtering to know if it was active
    if (wasUploading) {
      activeUploadsCountRef.current--;
      processQueue(); // Check if a new file can start
    }

    // Optional: Notify server to clean up partial uploads
    // axios.post('/api/cancel-upload', { fileId: id }).catch(err => console.warn("Cleanup request failed", err));
  };

  // --- Dropzone Setup ---
  const dropzoneOptions: DropzoneOptions = {
    onDrop,
    maxSize: MAX_FILE_SIZE,
    // accept: { 'image/*': ['.jpeg', '.png'] }, // Example: accept only specific image types
    multiple: true,
    disabled:
      Array.isArray(filesToUpload) &&
      filesToUpload.some((f) => f.status === "uploading" || f.status === "pending") &&
      activeUploadsCountRef.current >= SIMULTANEOUS_UPLOADS, // Optional: disable dropzone when busy
  };

  const { getRootProps, getInputProps, isDragActive, isFocused, isDragAccept, isDragReject } =
    useDropzone(dropzoneOptions);

  // --- Dynamic Dropzone Styling ---
  const dropzoneClassName = useCallback(() => {
    let base =
      "p-10 border-2 border-dashed rounded-md cursor-pointer text-center transition-colors duration-200 ease-in-out";
    if (isDragAccept) return `${base} border-green-500 bg-green-50`;
    if (isDragReject) return `${base} border-red-500 bg-red-50`;
    if (isDragActive) return `${base} border-blue-500 bg-blue-50`;
    return `${base} border-gray-300 hover:border-gray-400`;
  }, [isDragActive, isDragAccept, isDragReject]);

  // --- Render ---
  return (
    <div className="p-4 space-y-6">
      {/* Dropzone Area */}
      <div {...getRootProps({ className: dropzoneClassName() })}>
        <input {...getInputProps()} />
        <FiUploadCloud className="mx-auto text-4xl text-gray-400 mb-2" />
        {isDragAccept && <p className="text-green-600">Drop files here to upload</p>}
        {isDragReject && <p className="text-red-600">Some files will be rejected</p>}
        {!isDragActive && <p className="text-gray-500">Drag & drop files here, or click to select</p>}
        <p className="text-xs text-gray-400 mt-1">Max file size: {formatBytes(MAX_FILE_SIZE)}</p>
      </div>

      {/* File List Area */}
      {filesToUpload.length > 0 && (
        <div className="mt-4 space-y-3">
          <h3 className="text-xs font-small">
            Upload Queue (
            {
              filesToUpload.filter((f) => f.status !== "completed" && f.status !== "failed" && f.status !== "cancelled")
                .length
            }{" "}
            remaining)
          </h3>
          {filesToUpload.map((fileState) => (
            <div key={fileState.id} className="flex items-center p-3 bg-white border rounded-md shadow-sm space-x-3">
              {/* Icon */}
              <div className="flex-shrink-3 text-2xl">{getFileIcon(fileState.file.type)}</div>
              {/* File Info & Progress */}
              <div className="flex-grow overflow-hidden">
                <div className="text-sm font-medium text-gray-800 truncate" title={fileState.file.name}>
                  {fileState.file.name}
                </div>
                <div className="text-xs text-gray-500">{formatBytes(fileState.file.size)}</div>
                {/* Progress Bar */}
                {(fileState.status === "uploading" || fileState.status === "completed") && fileState.progress > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1 overflow-hidden">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ease-out ${
                        fileState.status === "completed" ? "bg-green-500" : "bg-blue-500"
                      }`}
                      style={{ width: `${fileState.progress}%` }}
                    ></div>
                  </div>
                )}
                {/* Status/Error Message */}
                {fileState.status === "failed" && (
                  <div className="text-xs text-red-600 mt-1 truncate" title={fileState.errorMessage}>
                    Error: {fileState.errorMessage || "Upload failed"}
                  </div>
                )}
                {fileState.status === "cancelled" && <div className="text-xs text-yellow-600 mt-1">Cancelled</div>}
                {(fileState.status === "pending" || fileState.status === "queued") && (
                  <div className="text-xs text-gray-500 mt-1">
                    {fileState.status === "queued" ? "Queued..." : "Starting..."}
                  </div>
                )}
              </div>
              {/* Action Button/Status Icon */}
              <div className="flex-shrink-0">
                {fileState.status === "completed8" ? (
                  <FiCheckCircle className="text-green-500 text-xl" />
                ) : fileState.status === "failed" ? (
                  <FiAlertCircle className="text-red-500 text-xl" />
                ) : (
                  // Show remove/cancel button for pending, queued, uploading, cancelled
                  <button
                    onClick={() => handleRemoveFile(fileState.id)}
                    className="p-1 text-gray-500 hover:text-red-600 rounded-full hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-50"
                    aria-label={`Remove ${fileState.file.name}`}
                    // disabled={fileState.status === 'completed' || fileState.status === 'failed'} // Redundant check as icon shown instead
                  >
                    <FiX className="text-lg" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChunkedUploader;
